import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, ArrowUp, ArrowDown, Package, FileText, Percent, Info } from 'lucide-react';
import Modal from '../components/Modal';
import './FormPage.css';
import { API_BASE_URL } from '../global';
import { showAlert, showConfirm } from '../components/AlertPopup';

export default function Stock() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('geral');
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const initialFormState = {
    id: null,
    produto_nome: '',
    produto_categoria: '',
    produto_unidade: 'un',
    produto_quantidade: '',
    produto_min_quantidade: '',
    produto_preco: '',
    // Campos Fiscais / SEFAZ NF-e
    ean: 'SEM GTIN',
    sem_gtin: true,
    ncm: '',
    cest: '',
    cfop_interno: '5102',
    cfop_externo: '6102',
    origem: '0',
    csosn: '102',
    cst_icms: '00',
    aliquota_icms: '0.00',
    cst_pis: '07',
    aliquota_pis: '0.00',
    cst_cofins: '07',
    aliquota_cofins: '0.00',
    cst_ipi: '53',
    aliquota_ipi: '0.00',
    codigo_enquadramento_ipi: '999'
  };

  const [formData, setFormData] = useState(initialFormState);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 30;

  useEffect(() => {
    fetchItems();
    fetchCategories();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const fetchItems = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/produtos.php`);
      const data = await response.json();
      setItems(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erro ao buscar produtos:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/categorias.php`);
      const data = await response.json();
      if (Array.isArray(data)) {
        setCategories(data);
      }
    } catch (error) {
      console.error('Erro ao buscar categorias:', error);
    }
  };

  const getStatus = (qtd, min) => {
    if (qtd <= 0) return 'Esgotado';
    if (qtd <= min) return 'Baixo';
    return 'Normal';
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'sem_gtin') {
      setFormData(prev => ({
        ...prev,
        sem_gtin: checked,
        ean: checked ? 'SEM GTIN' : ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const method = formData.id ? 'PUT' : 'POST';
    
    // Garantir que EAN esteja consistente se checkbox Sem GTIN estiver marcado
    const payload = {
      ...formData,
      ean: formData.sem_gtin ? 'SEM GTIN' : (formData.ean || 'SEM GTIN')
    };

    try {
      const response = await fetch(`${API_BASE_URL}/produtos.php`, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const result = await response.json();
      if (result.success) {
        setIsModalOpen(false);
        fetchItems();
        showAlert('Sucesso', formData.id ? 'Produto atualizado com sucesso!' : 'Produto cadastrado com sucesso!');
      } else {
        showAlert('Aviso', result.error || 'Erro ao salvar produto');
      }
    } catch (error) {
      console.error('Erro:', error);
      showAlert('Erro', 'Ocorreu um erro ao conectar com o servidor.');
    }
  };

  const handleEdit = (item) => {
    setFormData({
      id: item.id,
      produto_nome: item.produto_nome || '',
      produto_categoria: item.produto_categoria || '',
      produto_unidade: item.produto_unidade || 'un',
      produto_quantidade: item.produto_quantidade !== undefined ? item.produto_quantidade : '',
      produto_min_quantidade: item.produto_min_quantidade !== undefined ? item.produto_min_quantidade : '',
      produto_preco: item.produto_preco !== undefined ? item.produto_preco : '',
      ean: item.ean || 'SEM GTIN',
      sem_gtin: !item.ean || item.ean === 'SEM GTIN',
      ncm: item.ncm || '',
      cest: item.cest || '',
      cfop_interno: item.cfop_interno || '5102',
      cfop_externo: item.cfop_externo || '6102',
      origem: item.origem !== undefined && item.origem !== null ? String(item.origem) : '0',
      csosn: item.csosn || '102',
      cst_icms: item.cst_icms || '00',
      aliquota_icms: item.aliquota_icms || '0.00',
      cst_pis: item.cst_pis || '07',
      aliquota_pis: item.aliquota_pis || '0.00',
      cst_cofins: item.cst_cofins || '07',
      aliquota_cofins: item.aliquota_cofins || '0.00',
      cst_ipi: item.cst_ipi || '53',
      aliquota_ipi: item.aliquota_ipi || '0.00',
      codigo_enquadramento_ipi: item.codigo_enquadramento_ipi || '999'
    });
    setActiveTab('geral');
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    const confirmed = await showConfirm('Confirmação', 'Tem certeza que deseja remover este produto?');
    if (!confirmed) return;

    try {
      const response = await fetch(`${API_BASE_URL}/produtos.php?id=${id}`, {
        method: 'DELETE'
      });
      const result = await response.json();
      if (result.success) {
        fetchItems();
      } else {
        showAlert('Aviso', 'Erro ao excluir produto');
      }
    } catch (error) {
      console.error('Erro:', error);
    }
  };

  const openNewModal = () => {
    setFormData(initialFormState);
    setActiveTab('geral');
    setIsModalOpen(true);
  };

  const filteredItems = items.filter(item => 
    (item.produto_nome && item.produto_nome.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (item.id && item.id.toString().includes(searchTerm))
  );

  const totalPages = Math.ceil(filteredItems.length / itemsPerPage) || 1;
  const paginatedItems = filteredItems.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Estoque & Produtos</h1>
          <p className="page-subtitle">Acompanhe inventário, movimentações e dados fiscais NF-e 55</p>
        </div>
        <button className="btn-primary" onClick={openNewModal} style={{ width: 'auto', padding: '0.75rem 1.25rem' }}>
          <Plus size={18} />
          Adicionar Produto
        </button>
      </div>

      <div className="card list-card">
        <div className="card-header" style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <div className="search-bar" style={{ flex: '1', minWidth: '250px' }}>
            <Search size={18} className="search-icon" />
            <input 
              type="text" 
              placeholder="Buscar produto por nome ou código..." 
              className="search-input" 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn-outline" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <ArrowUp size={16} /> Entrada
            </button>
            <button className="btn-outline" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <ArrowDown size={16} /> Saída
            </button>
          </div>
        </div>
        
        <div className="table-responsive">
          <table className="data-table">
            <thead>
              <tr>
                <th>Produto / Categoria</th>
                <th>Preço</th>
                <th>Qtd. Atual</th>
                <th>NCM / EAN</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {paginatedItems.map((item) => {
                const status = getStatus(Number(item.produto_quantidade), Number(item.produto_min_quantidade));
                return (
                  <tr key={item.id}>
                    <td>
                      <div className="font-medium">{item.produto_nome}</div>
                      <div className="text-muted" style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>
                        {item.produto_categoria || 'Geral'} (Cód: {item.id})
                      </div>
                    </td>
                    <td>
                      <div className="font-medium">R$ {Number(item.produto_preco || 0).toFixed(2)}</div>
                    </td>
                    <td>
                      <div style={{ fontSize: '1.125rem', fontWeight: 'bold' }}>
                        {item.produto_quantidade} {item.produto_unidade}
                      </div>
                    </td>
                    <td>
                      <div style={{ fontSize: '0.85rem' }}>NCM: {item.ncm || 'Não Inf.'}</div>
                      <div className="text-muted" style={{ fontSize: '0.75rem' }}>GTIN: {item.ean || 'SEM GTIN'}</div>
                    </td>
                    <td>
                      <span className={`badge ${
                        status === 'Normal' ? 'badge-success' : 
                        status === 'Baixo' ? 'badge-warning' : 'badge-danger'
                      }`}>
                        {status}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button className="btn-icon" title="Editar Produto" onClick={() => handleEdit(item)}><Edit2 size={16} /></button>
                        <button className="btn-icon danger" title="Remover Produto" onClick={() => handleDelete(item.id)}><Trash2 size={16} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filteredItems.length === 0 && (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                    Nenhum produto encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* CONTROLES DE PAGINAÇÃO */}
        {filteredItems.length > 0 && (
          <div style={{
            display: 'flex',
            justify: 'space-between',
            alignItems: 'center',
            padding: '1rem 1.5rem',
            borderTop: '1px solid var(--border-color)',
            flexWrap: 'wrap',
            gap: '0.75rem'
          }}>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
              Exibindo {((currentPage - 1) * itemsPerPage) + 1} a {Math.min(currentPage * itemsPerPage, filteredItems.length)} de {filteredItems.length} produtos
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <button
                className="btn-outline"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                style={{ padding: '0.35rem 0.75rem', fontSize: '0.85rem', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', opacity: currentPage === 1 ? 0.5 : 1 }}
              >
                Anterior
              </button>
              <span style={{ fontSize: '0.85rem', fontWeight: '500', padding: '0 0.5rem', color: 'var(--text-main)' }}>
                Página {currentPage} de {totalPages}
              </span>
              <button
                className="btn-outline"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                style={{ padding: '0.35rem 0.75rem', fontSize: '0.85rem', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', opacity: currentPage === totalPages ? 0.5 : 1 }}
              >
                Próxima
              </button>
            </div>
          </div>
        )}
      </div>

      <Modal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)}
        title={formData.id ? "Editar Produto" : "Novo Produto - Cadastro Comercial e Fiscal"}
      >
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', width: '100%' }}>
          
          {/* Navegação por Abas no Modal */}
          <div className="form-tabs">
            <button 
              type="button" 
              className={`tab-btn ${activeTab === 'geral' ? 'active' : ''}`} 
              onClick={() => setActiveTab('geral')}
            >
              <Package size={16} /> Dados Gerais
            </button>
            <button 
              type="button" 
              className={`tab-btn ${activeTab === 'fiscal' ? 'active' : ''}`} 
              onClick={() => setActiveTab('fiscal')}
            >
              <FileText size={16} /> Fiscal & NF-e
            </button>
            <button 
              type="button" 
              className={`tab-btn ${activeTab === 'tributos' ? 'active' : ''}`} 
              onClick={() => setActiveTab('tributos')}
            >
              <Percent size={16} /> Tributação
            </button>
          </div>

          {/* ABA 1: DADOS GERAIS */}
          {activeTab === 'geral' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <div className="form-group flex-2">
                  <label>Nome do Produto *</label>
                  <input 
                    type="text" 
                    name="produto_nome" 
                    value={formData.produto_nome} 
                    onChange={handleInputChange} 
                    placeholder="Ex: Cadeira de Escritório Ergonômica" 
                    required 
                    className="standard-input" 
                  />
                </div>
                <div className="form-group flex-1">
                  <label>Categoria</label>
                  <select 
                    className="standard-input" 
                    name="produto_categoria" 
                    value={formData.produto_categoria} 
                    onChange={handleInputChange}
                  >
                    <option value="">Selecione...</option>
                    {categories.length > 0 ? (
                      categories.map(cat => (
                        <option key={cat.id || cat.categoria_id} value={cat.categoria_nome}>
                          {cat.categoria_nome}
                        </option>
                      ))
                    ) : (
                      <>
                        <option value="Equipamentos TI">Equipamentos TI</option>
                        <option value="Móveis">Móveis</option>
                        <option value="Papelaria">Papelaria</option>
                        <option value="Suprimentos">Suprimentos</option>
                      </>
                    )}
                  </select>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <div className="form-group flex-1">
                  <label>Unidade *</label>
                  <select 
                    className="standard-input" 
                    name="produto_unidade" 
                    value={formData.produto_unidade} 
                    onChange={handleInputChange} 
                    required
                  >
                    <option value="un">Unidade (un)</option>
                    <option value="cx">Caixa (cx)</option>
                    <option value="kg">Quilo (kg)</option>
                    <option value="g">Grama (g)</option>
                    <option value="l">Litro (l)</option>
                    <option value="ml">Mililitro (ml)</option>
                    <option value="m">Metro (m)</option>
                    <option value="m2">Metro Quadrado (m²)</option>
                    <option value="pc">Peça (pc)</option>
                    <option value="par">Par (par)</option>
                  </select>
                </div>
                <div className="form-group flex-1">
                  <label>Preço Unitário (R$) *</label>
                  <input 
                    type="number" 
                    step="0.01" 
                    min="0" 
                    name="produto_preco" 
                    value={formData.produto_preco} 
                    onChange={handleInputChange} 
                    placeholder="0.00" 
                    required 
                    className="standard-input" 
                  />
                </div>
                <div className="form-group flex-1">
                  <label>Estoque Atual *</label>
                  <input 
                    type="number" 
                    min="0" 
                    name="produto_quantidade" 
                    value={formData.produto_quantidade} 
                    onChange={handleInputChange} 
                    placeholder="0" 
                    required 
                    className="standard-input" 
                  />
                </div>
                <div className="form-group flex-1">
                  <label>Estoque Mínimo</label>
                  <input 
                    type="number" 
                    min="0" 
                    name="produto_min_quantidade" 
                    value={formData.produto_min_quantidade} 
                    onChange={handleInputChange} 
                    placeholder="0" 
                    className="standard-input" 
                  />
                </div>
              </div>
            </div>
          )}

          {/* ABA 2: FISCAL & NF-E */}
          {activeTab === 'fiscal' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              <div className="form-section-title">
                <Info size={16} /> Identificação e Classificação Fiscal
              </div>

              <div className="form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem' }}>
                  <label style={{ margin: 0 }}>Código EAN / GTIN (Código de Barras)</label>
                  <label className="form-checkbox-container">
                    <input 
                      type="checkbox" 
                      name="sem_gtin" 
                      checked={formData.sem_gtin} 
                      onChange={handleInputChange} 
                    />
                    <span>Sem GTIN / Código de Barras</span>
                  </label>
                </div>
                <input 
                  type="text" 
                  name="ean" 
                  value={formData.ean} 
                  onChange={handleInputChange} 
                  disabled={formData.sem_gtin}
                  placeholder={formData.sem_gtin ? "SEM GTIN" : "Ex: 7891234567890"} 
                  className="standard-input" 
                />
                <div className="form-hint">Marque "Sem GTIN" caso o produto não possua código de barras oficial EAN.</div>
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <div className="form-group flex-1">
                  <label>Código NCM (8 dígitos) *</label>
                  <input 
                    type="text" 
                    name="ncm" 
                    maxLength="8"
                    value={formData.ncm} 
                    onChange={handleInputChange} 
                    placeholder="Ex: 94033000" 
                    required={activeTab === 'fiscal'}
                    className="standard-input" 
                  />
                  <div className="form-hint">Nomenclatura Comum do Mercosul.</div>
                </div>

                <div className="form-group flex-1">
                  <label>Código CEST (Opcional - ST)</label>
                  <input 
                    type="text" 
                    name="cest" 
                    maxLength="7"
                    value={formData.cest} 
                    onChange={handleInputChange} 
                    placeholder="Ex: 0100100" 
                    className="standard-input" 
                  />
                  <div className="form-hint">Código Espec. da Substituição Tributária.</div>
                </div>
              </div>

              <div className="form-group">
                <label>Origem da Mercadoria *</label>
                <select 
                  className="standard-input" 
                  name="origem" 
                  value={formData.origem} 
                  onChange={handleInputChange}
                >
                  <option value="0">0 - Nacional (exceto as indicadas nos códigos 3 a 5 e 8)</option>
                  <option value="1">1 - Estrangeira - Importação direta</option>
                  <option value="2">2 - Estrangeira - Adquirida no mercado interno</option>
                  <option value="3">3 - Nacional com Conteúdo de Importação superior a 40%</option>
                  <option value="4">4 - Nacional produzida conforme processos produtivos básicos</option>
                  <option value="5">5 - Nacional com Conteúdo de Importação inferior ou igual a 40%</option>
                  <option value="6">6 - Estrangeira - Importação direta sem similar nacional</option>
                  <option value="7">7 - Estrangeira - Mercado interno sem similar nacional</option>
                  <option value="8">8 - Nacional - Conteúdo de Importação superior a 70%</option>
                </select>
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <div className="form-group flex-1">
                  <label>CFOP Operação Interna (Estado)</label>
                  <select 
                    className="standard-input" 
                    name="cfop_interno" 
                    value={formData.cfop_interno} 
                    onChange={handleInputChange}
                  >
                    <option value="5101">5101 - Venda de produção do estabelecimento</option>
                    <option value="5102">5102 - Venda de mercadoria adquirida de terceiros</option>
                    <option value="5405">5405 - Venda de mercadoria c/ ST</option>
                    <option value="5949">5949 - Outra saída de mercadoria</option>
                  </select>
                </div>
                <div className="form-group flex-1">
                  <label>CFOP Operação Interestadual (Fora)</label>
                  <select 
                    className="standard-input" 
                    name="cfop_externo" 
                    value={formData.cfop_externo} 
                    onChange={handleInputChange}
                  >
                    <option value="6101">6101 - Venda de produção fora do estado</option>
                    <option value="6102">6102 - Venda de mercadoria adquirida de terceiros</option>
                    <option value="6405">6405 - Venda de mercadoria c/ ST fora do estado</option>
                    <option value="6949">6949 - Outra saída fora do estado</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* ABA 3: TRIBUTAÇÃO */}
          {activeTab === 'tributos' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              <div className="form-section-title">
                <Percent size={16} /> Alíquotas e Enquadramento Tributário
              </div>

              {/* ICMS / Simples Nacional */}
              <div style={{ display: 'flex', gap: '1rem' }}>
                <div className="form-group flex-2">
                  <label>CSOSN (Simples Nacional)</label>
                  <select 
                    className="standard-input" 
                    name="csosn" 
                    value={formData.csosn} 
                    onChange={handleInputChange}
                  >
                    <option value="101">101 - Tributada pelo Simples com permissão de crédito</option>
                    <option value="102">102 - Tributada pelo Simples sem permissão de crédito</option>
                    <option value="201">201 - Tributada com permissão de crédito e cobrança ST</option>
                    <option value="202">202 - Tributada sem permissão de crédito e cobrança ST</option>
                    <option value="500">500 - ICMS cobrado anteriormente por substituição tributária</option>
                    <option value="900">900 - Outros</option>
                  </select>
                </div>
                <div className="form-group flex-1">
                  <label>Alíquota ICMS (%)</label>
                  <input 
                    type="number" 
                    step="0.01" 
                    min="0" 
                    name="aliquota_icms" 
                    value={formData.aliquota_icms} 
                    onChange={handleInputChange} 
                    placeholder="0.00" 
                    className="standard-input" 
                  />
                </div>
              </div>

              {/* PIS & COFINS */}
              <div style={{ display: 'flex', gap: '1rem' }}>
                <div className="form-group flex-1">
                  <label>CST PIS</label>
                  <select 
                    className="standard-input" 
                    name="cst_pis" 
                    value={formData.cst_pis} 
                    onChange={handleInputChange}
                  >
                    <option value="01">01 - Operação Tributável Alíquota Normal</option>
                    <option value="07">07 - Operação Isenta da Contribuição</option>
                    <option value="08">08 - Operação Sem Incidência</option>
                    <option value="49">49 - Outras Operações de Saída</option>
                  </select>
                </div>
                <div className="form-group flex-1">
                  <label>Alíquota PIS (%)</label>
                  <input 
                    type="number" 
                    step="0.01" 
                    min="0" 
                    name="aliquota_pis" 
                    value={formData.aliquota_pis} 
                    onChange={handleInputChange} 
                    placeholder="0.00" 
                    className="standard-input" 
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <div className="form-group flex-1">
                  <label>CST COFINS</label>
                  <select 
                    className="standard-input" 
                    name="cst_cofins" 
                    value={formData.cst_cofins} 
                    onChange={handleInputChange}
                  >
                    <option value="01">01 - Operação Tributável Alíquota Normal</option>
                    <option value="07">07 - Operação Isenta da Contribuição</option>
                    <option value="08">08 - Operação Sem Incidência</option>
                    <option value="49">49 - Outras Operações de Saída</option>
                  </select>
                </div>
                <div className="form-group flex-1">
                  <label>Alíquota COFINS (%)</label>
                  <input 
                    type="number" 
                    step="0.01" 
                    min="0" 
                    name="aliquota_cofins" 
                    value={formData.aliquota_cofins} 
                    onChange={handleInputChange} 
                    placeholder="0.00" 
                    className="standard-input" 
                  />
                </div>
              </div>

              {/* IPI */}
              <div style={{ display: 'flex', gap: '1rem' }}>
                <div className="form-group flex-1">
                  <label>CST IPI</label>
                  <select 
                    className="standard-input" 
                    name="cst_ipi" 
                    value={formData.cst_ipi} 
                    onChange={handleInputChange}
                  >
                    <option value="50">50 - Saída Tributada</option>
                    <option value="53">53 - Saída Não Tributada</option>
                    <option value="99">99 - Outras Saídas</option>
                  </select>
                </div>
                <div className="form-group flex-1">
                  <label>Alíquota IPI (%)</label>
                  <input 
                    type="number" 
                    step="0.01" 
                    min="0" 
                    name="aliquota_ipi" 
                    value={formData.aliquota_ipi} 
                    onChange={handleInputChange} 
                    placeholder="0.00" 
                    className="standard-input" 
                  />
                </div>
                <div className="form-group flex-1">
                  <label>Cód. Enquadr. IPI</label>
                  <input 
                    type="text" 
                    name="codigo_enquadramento_ipi" 
                    value={formData.codigo_enquadramento_ipi} 
                    onChange={handleInputChange} 
                    placeholder="999" 
                    className="standard-input" 
                  />
                </div>
              </div>
            </div>
          )}

          <div className="form-actions" style={{ justifyContent: 'flex-end', gap: '1rem', marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--border-color)' }}>
            <button type="button" className="btn-outline" onClick={() => setIsModalOpen(false)}>Cancelar</button>
            <button type="submit" className="btn-success">Salvar Produto</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
